import { months } from './Constants';

export const HISTORY_SAVED_LOCATION_STORAGE_KEY = 'history-saved-location';

const getSessionSelectionSuffix = (item = {}) =>
  [item.userTime, item.creationTime, item.sort, item.order, JSON.stringify(item.filters || [])].filter(Boolean).join('|');

export const getHistorySelectionKey = (item = {}) => {
  const baseKey = item.id || item.resultName;
  const shouldUseSessionIdentity = item.isSessionOnlyRecent || item.filters?.length || item.sort || item.order;
  if (!shouldUseSessionIdentity) {
    return baseKey;
  }
  const suffix = getSessionSelectionSuffix(item);
  return suffix ? `${baseKey}|${suffix}` : baseKey;
};

const dateIsValid = (date) => date instanceof Date && !Number.isNaN(date.getTime());

export const formatDate = (creationDate) => {
  if (!dateIsValid(creationDate)) return '';
  const month = months[creationDate.getMonth()].substring(0, 3);
  const year = creationDate.getFullYear();
  const day = creationDate.getDate();
  return `${month} ${day} ${year}`;
};

export const addZero = (num) => (num < 10 ? `0${num}` : num);

const parseTimestamp = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    // Backend format for userTime is "YYYY/MM/DD HH:mm:ss" and represents UTC.
    // Parse it explicitly as UTC so UI formatting can convert it to local time.
    const utcMatch = value.match(/^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
    if (utcMatch) {
      const [, year, month, day, hours, minutes, seconds] = utcMatch;
      return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds));
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const getHistoryDisplayTime = (historyItem = {}) => {
  const userTimestamp = parseTimestamp(historyItem?.userTime);
  if (userTimestamp !== null) {
    return userTimestamp;
  }

  return parseTimestamp(historyItem?.creationTime);
};

// Saved searches are ordered by their save-moment (`userTime`), not the original search creationTime.
export const getSavedHistoryTime = (historyItem = {}) => {
  const savedTimestamp = parseTimestamp(historyItem?.userTime);
  if (savedTimestamp !== null) {
    return savedTimestamp;
  }

  return parseTimestamp(historyItem?.creationTime);
};

const getCreationDate = (creationTime) => {
  const epochTime = parseTimestamp(creationTime);
  if (epochTime === null) {
    return null;
  }
  const creationDate = new Date(epochTime);
  return dateIsValid(creationDate) ? creationDate : null;
};

export const formatHistoryMetadataDate = (creationTime) => {
  const creationDate = getCreationDate(creationTime);
  if (!creationDate) return '';

  return `${formatDate(creationDate)}, at ${addZero(creationDate.getHours())}:${addZero(creationDate.getMinutes())}`;
};

// Saved searches show the date only (no time), e.g. "Oct 15 2025".
export const formatHistorySavedDate = (creationTime) => {
  const creationDate = getCreationDate(creationTime);
  if (!creationDate) return '';
  return formatDate(creationDate);
};

export const capitalizeFirstLetter = (input) =>
  typeof input === 'string' && input.length > 0 ? input.charAt(0).toUpperCase() + input.slice(1) : input;

export const getDateGroupLabel = (creationTime) => {
  const creationDate = getCreationDate(creationTime);
  if (!creationDate) return '';
  const todayDate = new Date(Date.now());
  const formattedCreatedDate = formatDate(creationDate);
  const formattedTodayDate = formatDate(todayDate);
  if (formattedCreatedDate === formattedTodayDate) {
    return 'Today';
  }
  // Group by "Month Year" so all searches from the same month are under one heading
  const month = months[creationDate.getMonth()];
  const year = creationDate.getFullYear();
  return `${month} ${year}`;
};
